/**
 * FicheTabRelations.tsx
 * ──────────────────────
 * Onglet "Relations" : procédure, historique, citations, notions.
 */
import { getDateLocale } from '@/i18n';
import { articleLabel, articlePathFromFiche } from '@/utils/articleSlug';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '@/components/Loader';

import { CiteArretCard } from './CiteArretCard';
import {
  CITE_TYPE_CONFIG,
  IMPACT_CONFIG,
  PROC_LINK_CONFIG
} from './citeTypeConfig';
import type { CiteType, ProcedureLink, PublicFiche, Relations } from './types';

type RelTab = 'procedure' | 'historique' | 'citations' | 'notions';

interface Props {
  fiche: PublicFiche;
  relations: Relations | null;
  loadingRel: boolean;
  blocked: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const SOURCE_LABEL: Record<string, string> = {
  judilibre_contested: 'Judilibre',
  judilibre_timeline: 'Judilibre',
  judilibre_forward: 'Judilibre',
  judilibre_rapprochement: 'Judilibre',
  judilibre_numbers: 'Judilibre',
  llm: 'Legal Graph',
  build_procedure_graph: 'Legal Graph'
};

const expandChamber = (v: string | null | undefined, t: TFunction) => {
  if (!v) return '';
  // Clés i18n fiche.chamber.*
  const MAP: Record<string, string> = {
    comm: 'com',
    com: 'com',
    soc: 'soc',
    crim: 'crim',
    civ1: 'civ1',
    civ2: 'civ2',
    civ3: 'civ3',
    pl: 'pl',
    mi: 'mi',
    fs: 'fs',
    fp: 'fp'
  };
  const key = MAP[v.toLowerCase()];
  return key ? t(`fiche.chamber.${key}`) : v;
};

// ── ProcedureLinkCard ─────────────────────────────────────────────────────────

const ProcedureLinkCard = ({ link }: { link: ProcedureLink }) => {
  const { t } = useTranslation();
  const known = PROC_LINK_CONFIG[link.link_type];
  const cfg = known
    ? { label: t(known.labelKey), color: known.color, icon: known.icon }
    : {
        label: link.link_type,
        color: 'bg-muted text-muted-foreground',
        icon: '→'
      };

  const inner = (
    <div className="p-4 rounded-xl border bg-card text-sm space-y-1 hover:border-primary/40 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${cfg.color}`}
        >
          {cfg.icon} {cfg.label}
        </span>
        <span className="text-xs text-muted-foreground/60 font-mono">
          {Math.round(link.confidence * 100)}% ·{' '}
          {SOURCE_LABEL[link.source] ?? link.source}
        </span>
      </div>
      <p className="font-medium">{link.target}</p>
      {link.resolved ? (
        <div className="space-y-0.5">
          <p className="text-xs text-muted-foreground">
            {expandChamber(link.target_chamber, t)}
            {link.target_decision_date ? ` — ${link.target_decision_date}` : ''}
            {link.target_solution ? ` · ${link.target_solution}` : ''}
          </p>
          {link.target_probleme && (
            <p className="text-xs text-muted-foreground italic line-clamp-2">
              {link.target_probleme}
            </p>
          )}
          {!link.target_slug && (
            <p className="text-xs text-muted-foreground/50 italic mt-1">
              {t('fiche.rel.not_indexed_sl')}
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground/50 italic">
          {t('fiche.rel.not_indexed_db')}
        </p>
      )}
    </div>
  );

  if (link.target_slug) {
    return (
      <a
        href={`/jurisprudence/${link.target_slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
      >
        {inner}
      </a>
    );
  }
  return inner;
};

// ── ArticleLinks ──────────────────────────────────────────────────────────────

const ArticleLinks = ({
  articles
}: {
  articles: Array<{
    code: string;
    article: string;
    node_id?: string | null;
    slug_code?: string | null;
  }>;
}) => (
  <div className="flex flex-wrap gap-2">
    {articles.map((a, i) => {
      const path = articlePathFromFiche(a);
      if (!path) {
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1.5 text-sm font-mono bg-muted text-muted-foreground px-3 py-1.5 rounded-full"
          >
            {articleLabel(a.code, a.article)}
          </span>
        );
      }
      return (
        <Link
          key={i}
          target="_blank"
          rel="noopener noreferrer"
          to={path}
          className="inline-flex items-center gap-1.5 text-sm font-mono bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
        >
          {articleLabel(a.code, a.article)}
          <svg
            className="w-3 h-3 opacity-60"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </Link>
      );
    })}
  </div>
);

// ── Composant principal ───────────────────────────────────────────────────────

export function FicheTabRelations({
  relations,
  loadingRel,
  blocked
}: Props) {
  const { t } = useTranslation();
  const [relTab, setRelTab] = useState<RelTab>('procedure');

  if (loadingRel)
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader className="!size-8" />
        <p className="text-sm text-muted-foreground">
          {t('fiche.rel.loading')}
        </p>
      </div>
    );

  if (!relations)
    return (
      <div className="text-center py-24 text-muted-foreground">
        <p className="text-5xl mb-4">🔍</p>
        <p className="font-semibold text-foreground mb-1">
          {t('fiche.rel.unavailable')}
        </p>
        <p className="text-sm">{t('fiche.rel.not_enriched')}</p>
      </div>
    );

  return (
    <div className="space-y-5">
      {/* Tabs internes */}
      <div className="flex gap-1 border-b">
        {(
          [
            [
              'procedure',
              t('fiche.rel.tab_procedure'),
              (relations.procedure_graph?.length ?? 0) +
                (relations.cited_by_count ?? 0)
            ],
            [
              'historique',
              t('fiche.rel.tab_history'),
              relations.arrets_impactes?.length ?? 0
            ],
            [
              'citations',
              t('fiche.rel.tab_citations'),
              relations.cite_arrets?.length ?? 0
            ],
            [
              'notions',
              t('fiche.rel.tab_notions'),
              (relations.concepts?.length ?? 0) +
                (relations.articles?.length ?? 0)
            ]
          ] as [RelTab, string, number][]
        ).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setRelTab(key)}
            className={`px-3 py-1.5 text-xs rounded-t-md -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${
              relTab === key
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
            {count > 0 && (
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full font-mono">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ Procédure ═══ */}
      {relTab === 'procedure' && (
        <div className="space-y-6 pt-2">
          {(relations.importance_score > 0 ||
            relations.cited_by_count > 0 ||
            (relations.procedure_graph?.length ?? 0) > 0) && (
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 text-center">
                <p className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
                  {relations.importance_score}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('fiche.rel.impact_score')}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 text-center">
                <p className="text-xl font-bold text-yellow-700 dark:text-yellow-300">
                  {relations.cited_by_count}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('fiche.rel.incoming')}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-center">
                <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                  {relations.procedure_graph?.length ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('fiche.rel.proc_links_count')}
                </p>
              </div>
            </div>
          )}

          {(relations.procedure_graph?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.proc_links')}
                {relations.graph_built_at && (
                  <span className="ml-2 font-normal normal-case opacity-60">
                    ·{' '}
                    {new Date(relations.graph_built_at).toLocaleDateString(
                      getDateLocale()
                    )}
                  </span>
                )}
              </h3>
              <div className="space-y-3">
                {relations.procedure_graph.map((link, i) => (
                  <ProcedureLinkCard key={link.link_id || i} link={link} />
                ))}
              </div>
            </div>
          )}

          {(relations.cited_by_fiches?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.cited_by', { count: relations.cited_by_count })}
                {relations.cited_by_count > 20 && (
                  <span className="font-normal normal-case ml-1 opacity-60">
                    {t('fiche.rel.top20')}
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                {relations.cited_by_fiches.map((cbf, i) => (
                  <a
                    key={cbf.id || i}
                    href={cbf.slug ? `/jurisprudence/${cbf.slug}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-xl border bg-card text-sm hover:border-primary/40 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-semibold">
                            {cbf.number || cbf.ecli}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {expandChamber(cbf.chamber, t)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {cbf.decision_date}
                          </span>
                          {cbf.domaine && (
                            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 rounded">
                              {cbf.domaine}
                            </span>
                          )}
                        </div>
                      </div>
                      <svg
                        className="w-4 h-4 shrink-0 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {!relations.procedure_graph?.length && !relations.cited_by_count && (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-4xl mb-3">⬡</p>
              <p className="text-sm">{t('fiche.rel.no_proc_graph')}</p>
            </div>
          )}
        </div>
      )}

      {/* ═══ Historique ═══ */}
      {relTab === 'historique' && (
        <div className="space-y-6 pt-2">
          {relations.procedure_lineage &&
            Object.keys(relations.procedure_lineage).length > 0 &&
            (() => {
              const pl = relations.procedure_lineage;
              const hasAttaque =
                !!pl.arret_attaque &&
                (pl.arret_attaque.juridiction || pl.arret_attaque.ref);
              const hasRenvoi = !!pl.ca_renvoi?.juridiction;
              const hasJoints = (pl.pourvois_joints?.length ?? 0) > 0;
              const hasAnterieurs =
                (pl.arrets_anterieurs_meme_affaire?.length ?? 0) > 0;
              if (!hasAttaque && !hasRenvoi && !hasJoints && !hasAnterieurs)
                return null;
              return (
                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    {t('fiche.rel.lineage')}
                  </h3>
                  {hasAttaque && pl.arret_attaque && (
                    <div className="p-4 rounded-xl border bg-muted/20">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {t('fiche.rel.contested')}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        {pl.arret_attaque.juridiction && (
                          <span className="font-medium">
                            {pl.arret_attaque.juridiction}
                          </span>
                        )}
                        {pl.arret_attaque.date && (
                          <span className="text-muted-foreground">
                            {pl.arret_attaque.date}
                          </span>
                        )}
                        {pl.arret_attaque.ref && (
                          <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                            {pl.arret_attaque.ref}
                          </span>
                        )}
                        {pl.arret_attaque.solution_cc && (
                          <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">
                            {pl.arret_attaque.solution_cc}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {hasRenvoi && pl.ca_renvoi && (
                    <div className="p-4 rounded-xl border bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                        {t('fiche.rel.renvoi')}
                      </p>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        {t('fiche.rel.ca_of', {
                          city: pl.ca_renvoi.juridiction
                        })}
                      </p>
                      {pl.ca_renvoi.mention_textuelle && (
                        <p className="text-xs text-muted-foreground italic mt-1">
                          "{pl.ca_renvoi.mention_textuelle}"
                        </p>
                      )}
                    </div>
                  )}
                  {hasJoints && (
                    <div className="p-4 rounded-xl border bg-muted/20">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {t('fiche.rel.joined')}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {pl.pourvois_joints!.map((ref, i) => (
                          <span
                            key={i}
                            className="font-mono text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded"
                          >
                            {ref}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {hasAnterieurs && (
                    <div className="p-4 rounded-xl border bg-muted/20">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {t('fiche.rel.earlier')}
                      </p>
                      <div className="space-y-1.5">
                        {pl.arrets_anterieurs_meme_affaire!.map((a, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 text-xs"
                          >
                            <span className="text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {a.etape}
                            </span>
                            <span className="font-mono">{a.ref}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

          {(relations.arrets_impactes?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.impacted', {
                  count: relations.arrets_impactes.length
                })}
              </h3>
              <div className="space-y-3">
                {relations.arrets_impactes.map((ai, i) => {
                  const known = IMPACT_CONFIG[ai.impact];
                  const cfg = known
                    ? {
                        label: t(known.labelKey),
                        color: known.color,
                        icon: known.icon
                      }
                    : {
                        label: ai.impact,
                        color: 'bg-muted text-muted-foreground',
                        icon: '→'
                      };
                  return (
                    <div
                      key={i}
                      className="p-4 rounded-xl border bg-card text-sm"
                    >
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${cfg.color}`}
                        >
                          {cfg.icon} {cfg.label}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {ai.ref}
                        </span>
                      </div>
                      {ai.explication && (
                        <p className="text-xs text-muted-foreground italic">
                          {ai.explication}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!relations.procedure_lineage?.arret_attaque?.juridiction &&
            !relations.arrets_impactes?.length && (
              <div className="text-center py-16 text-muted-foreground">
                <p className="text-4xl mb-3">⎇</p>
                <p className="text-sm">{t('fiche.rel.no_history')}</p>
              </div>
            )}
        </div>
      )}

      {/* ═══ Citations ═══ */}
      {relTab === 'citations' && (
        <div className="space-y-6 pt-2">
          {(relations.citation_count ?? 0) > 0 && (
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 flex items-center gap-3">
              <span className="text-2xl">★</span>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                <Trans
                  i18nKey="fiche.rel.cited_count"
                  count={relations.citation_count}
                  components={{ b: <span className="font-bold" /> }}
                />
              </p>
            </div>
          )}
          {(relations.cite_arrets_qualifies?.length ?? 0) > 0 ? (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.cited_decisions_lg', {
                  count: relations.cite_arrets_qualifies.length
                })}
              </h3>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(
                  Object.entries(CITE_TYPE_CONFIG) as [
                    CiteType,
                    (typeof CITE_TYPE_CONFIG)[CiteType]
                  ][]
                ).map(([type, cfg]) => {
                  const count = relations.cite_arrets_qualifies.filter(
                    (c) => c.type === type
                  ).length;
                  if (!count) return null;
                  return (
                    <span
                      key={type}
                      className={`text-xs px-2.5 py-1 rounded-full font-medium ${cfg.color}`}
                    >
                      {cfg.icon} {t(cfg.labelKey)} ({count})
                    </span>
                  );
                })}
              </div>
              <div className="space-y-3">
                {relations.cite_arrets_qualifies.map((cite, i) => (
                  <CiteArretCard key={i} cite={cite} />
                ))}
              </div>
            </div>
          ) : (relations.cite_arrets?.length ?? 0) > 0 ? (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.cited_decisions', {
                  count: relations.cite_arrets.length
                })}
              </h3>
              <div className="space-y-2">
                {relations.cite_arrets.map((ref, i) => {
                  const found = relations.cited_fiches?.find(
                    (f) => f.ecli === ref || f.number === ref
                  );
                  return (
                    <div
                      key={i}
                      className={`p-4 rounded-xl border text-sm ${
                        found
                          ? 'bg-card hover:border-primary/30'
                          : 'bg-muted/30'
                      }`}
                    >
                      <p className="font-mono text-xs text-muted-foreground mb-1">
                        {ref}
                      </p>
                      {found && (
                        <>
                          <p className="font-semibold text-xs">
                            {found.chamber} — {found.decision_date}
                          </p>
                          {found.probleme && (
                            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
                              {found.probleme}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-4xl mb-3">⭐</p>
              <p className="text-sm">{t('fiche.rel.no_citations')}</p>
            </div>
          )}
          {blocked && (
            <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-8 text-center">
              <h3 className="font-bold mb-2">{t('fiche.rel.paywall_title')}</h3>
              <p className="text-sm text-muted-foreground mb-5">
                {t('fiche.rel.paywall_desc')}
              </p>
              <Link
                to="/login"
                className="inline-flex px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
              >
                {t('fiche.rel.paywall_cta')}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ═══ Notions ═══ */}
      {relTab === 'notions' && (
        <div className="space-y-6 pt-2">
          {(relations.concepts?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.tab.concepts')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {relations.concepts.map((c, i) => (
                  <span
                    key={i}
                    className="text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-3 py-1.5 rounded-full"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(relations.articles?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.tab.articles')}
              </h3>
              <ArticleLinks articles={relations.articles} />
            </div>
          )}
          {(relations.mots_cles?.length ?? 0) > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
                {t('fiche.rel.keywords')}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {relations.mots_cles.map((m, i) => (
                  <span
                    key={i}
                    className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-lg"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
          {!relations.concepts?.length && !relations.articles?.length && (
            <div className="text-center py-16 text-muted-foreground">
              <p className="text-4xl mb-3">🏷</p>
              <p className="text-sm">{t('fiche.rel.no_notions')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
